import { getCurrentDate } from './timeUtils';

/**
 * Returns the ISO week number and year for a given date.
 * ISO week: week starts on Monday, week 1 = week containing first Thursday of year.
 */
export function getISOWeek(date: Date): { week: number; year: number } {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7; // Make Sunday = 7
  d.setUTCDate(d.getUTCDate() + 4 - dayNum); // Nearest Thursday
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const week = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return { week, year: d.getUTCFullYear() };
}

/**
 * Returns a formatted week string like "Uge 12" for the current week.
 */
export function getCurrentWeekLabel(): string {
  const { week } = getISOWeek(getCurrentDate());
  return `Uge ${week}`;
}

/**
 * Returns a formatted week string like "Uge 11" for the previous week.
 */
export function getPrevWeekLabel(): string {
  const now = getCurrentDate();
  const prevWeekDate = new Date(now);
  prevWeekDate.setDate(now.getDate() - 7);
  const { week } = getISOWeek(prevWeekDate);
  return `Uge ${week}`;
}

/**
 * Returns a human-readable date string for the current day.
 * e.g. "Mandag, 21. marts · Uge 12"
 */
export function getCurrentDateLabel(): string {
  const now = getCurrentDate();
  const { week } = getISOWeek(now);

  const days = ['Søndag', 'Mandag', 'Tirsdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lørdag'];
  const months = [
    'januar', 'februar', 'marts', 'april', 'maj', 'juni',
    'juli', 'august', 'september', 'oktober', 'november', 'december',
  ];

  const dayName = days[now.getDay()];
  const dayNum = now.getDate();
  const monthName = months[now.getMonth()];

  return `${dayName}, ${dayNum}. ${monthName} · Uge ${week}`;
}

/**
 * Returns the current week_id in YYYY-WW format (e.g. "2026-W12").
 * Used for database deduplication of check-ins.
 */
export function getCurrentWeekId(): string {
  const { week, year } = getISOWeek(getCurrentDate());
  return `${year}-W${String(week).padStart(2, '0')}`;
}

/**
 * Returns the previous week_id in YYYY-WW format (e.g. "2026-W11").
 */
export function getPrevWeekId(): string {
  const now = getCurrentDate();
  const prevWeekDate = new Date(now);
  prevWeekDate.setDate(now.getDate() - 7);
  const { week, year } = getISOWeek(prevWeekDate);
  return `${year}-W${String(week).padStart(2, '0')}`;
}

/**
 * Returns an array of week_ids going back N weeks from today.
 * e.g. getWeekIdsForRange(4) → ["2026-W09", "2026-W10", "2026-W11", "2026-W12"]
 */
export function getWeekIdsForRange(numWeeks: number): string[] {
  const result: string[] = [];
  const now = getCurrentDate();
  for (let i = numWeeks - 1; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(now.getDate() - i * 7);
    const { week, year } = getISOWeek(d);
    result.push(`${year}-W${String(week).padStart(2, '0')}`);
  }
  // Deduplicate (in case of edge cases)
  return [...new Set(result)];
}
